import { useLogout, useUserEmailIfLoggedIn } from '@/auth/authentication';
import { DrawerItemConfig } from '@/components/GlobalNavigationDrawer/lib/types';
import { useGlobalDrawerItems } from '@/components/GlobalNavigationDrawer/lib/useGlobalDrawerItems';
import {
    paletteUnavailableAtom,
    requestPaletteOpen,
} from '@/components/commandPalette/paletteOpenRequest';
import { modifierKey } from '@/components/commandPalette/paletteShortcut';
import { FeatureFlag } from '@/types/featureFlag';
import { ReturnComponent } from '@/utils/reactTypes';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import { Divider, List } from '@mui/material';
import { useAtomValue } from 'jotai';
import { Fragment } from 'react';

import { GlobalDrawerHeader } from './GlobalDrawerHeader';
import { GlobalDrawerItem } from './GlobalDrawerItem';

const SEARCH_ITEM: DrawerItemConfig = {
    label: 'Search',
    icon: <SearchOutlined />,
    canAccess: true,
    onClick: requestPaletteOpen,
    shortcutHint: `${modifierKey()} + K`,
};

export const GlobalDrawerItems = (): ReturnComponent => {
    const currentUserEmail = useUserEmailIfLoggedIn();

    const logout = useLogout();
    // A palette that failed to load cannot open, so the item is not offered.
    const paletteUnavailable = useAtomValue(paletteUnavailableAtom);
    const hasCommandPalette =
        useFeatureFlagEnabled(FeatureFlag.DASHBOARD_COMMAND_PALETTE_MVP) && !paletteUnavailable;

    const DRAWER_ITEMS = useGlobalDrawerItems();

    return (
        <>
            <GlobalDrawerHeader />
            <List
                component={'nav'}
                sx={{
                    bgcolor: 'transparent',
                }}
            >
                {DRAWER_ITEMS.filter((item) => item.canAccess).map((item) => (
                    <Fragment key={item.label}>
                        {item.isDivider && <Divider sx={{ mx: 3, my: 1 }} />}
                        <GlobalDrawerItem item={item} />
                    </Fragment>
                ))}
                {hasCommandPalette && (
                    <>
                        <Divider sx={{ mx: 3, my: 1 }} />
                        <GlobalDrawerItem item={SEARCH_ITEM} />
                    </>
                )}
                <Divider sx={{ mx: 3, my: 1 }} />
                <GlobalDrawerItem
                    item={{
                        label: 'Logout',
                        subLabel: currentUserEmail ?? undefined,
                        icon: <LogoutOutlined />,
                        canAccess: true,
                        onClick: logout,
                    }}
                />
            </List>
        </>
    );
};
